'use client'

import { useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'

import '@/lib/altcha-locale'

import type { AltchaWidgetElement } from 'altcha'
import { ArrowRight, Send, Star } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { captchaBypassEnabled } from '@/lib/captcha-bypass'
import { googleReviewsUrl } from '@/lib/config'
import type { ContextResponseData } from '@/lib/context-response'
import { feedbackSchema, type FeedbackFormData } from '@/lib/schemas/feedback'
import { useCsrf } from '@/hooks/use-csrf'
import { Button } from '@/components/ui/button'
import { ContextResponse } from '@/components/context-response'
import { CsrfRetry } from '@/components/csrf-retry'
import { GoogleLogo } from '@/components/google-logo'

type Stage = 'ask' | 'support' | 'thanks'

const GOOGLE_REVIEWS_URL = googleReviewsUrl

export function ReviewGate() {
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [stage, setStage] = useState<Stage>('ask')
  const [response, setResponse] = useState<ContextResponseData | null>(null)
  const { token: csrfToken } = useCsrf()
  const [captchaPayload, setCaptchaPayload] = useState(captchaBypassEnabled ? 'bypass' : '')
  const [website, setWebsite] = useState('')
  const [captchaMounted, setCaptchaMounted] = useState(false)
  const captchaRef = useRef<AltchaWidgetElement>(null)
  const inFlight = useRef(false)

  useEffect(() => {
    // Defer widget render to after hydration so the Web Component's
    // connectedCallback does not race the SSR diff.
    if (!captchaBypassEnabled) setCaptchaMounted(true)
  }, [])

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    mode: 'onTouched',
    shouldFocusError: true,
    delayError: 300,
    defaultValues: {
      rating: 0,
      message: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  })

  // Attach captcha event listeners when the widget mounts (during 'support' stage)
  useEffect(() => {
    if (captchaBypassEnabled || !captchaMounted) return
    const el = captchaRef.current
    if (!el) return
    const handleVerified = (e: Event) => {
      const detail = (e as CustomEvent<{ payload: string }>).detail
      setCaptchaPayload(detail?.payload ?? '')
    }
    const clearPayload = () => setCaptchaPayload('')
    el.addEventListener('verified', handleVerified)
    el.addEventListener('expired', clearPayload)
    el.addEventListener('error', clearPayload)
    return () => {
      el.removeEventListener('verified', handleVerified)
      el.removeEventListener('expired', clearPayload)
      el.removeEventListener('error', clearPayload)
    }
  }, [captchaMounted, stage])

  function handleSelect(stars: number) {
    setRating(stars)
    setValue('rating', stars)

    if (stars >= 4) {
      window.open(GOOGLE_REVIEWS_URL, '_blank', 'noopener,noreferrer')
    } else {
      setStage('support')
    }
  }

  const onSubmit = async (data: FeedbackFormData) => {
    if (!csrfToken || inFlight.current) return
    inFlight.current = true
    setResponse(null)

    try {
      const res = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': csrfToken,
        },
        body: JSON.stringify({ ...data, captchaPayload, website }),
      })

      const result = (await res.json().catch(() => ({}))) as Partial<ContextResponseData>

      if (res.ok) {
        setResponse({
          severity: result.severity ?? 'success',
          text: result.text ?? 'Thanks for the feedback. Paul will be in touch.',
        })
        captchaRef.current?.reset()
        setCaptchaPayload('')
        setStage('thanks')
      } else {
        setResponse({
          severity: result.severity ?? 'danger',
          text: result.text ?? 'Something went wrong. Please try again.',
        })
        // The route consumes the altcha proof on verification even when it
        // then rejects the submission, so the spent payload needs a reset.
        captchaRef.current?.reset()
        setCaptchaPayload(captchaBypassEnabled ? 'bypass' : '')
      }
    } catch {
      setResponse({
        severity: 'danger',
        text: 'Could not reach the server. Check your connection and try again.',
      })
      captchaRef.current?.reset()
      setCaptchaPayload(captchaBypassEnabled ? 'bypass' : '')
    } finally {
      inFlight.current = false
    }
  }

  const activeStars = hover || rating

  return (
    <div className="flex flex-col items-center gap-3">
      {stage === 'ask' && (
        <>
          <p className="font-display text-2xl font-bold text-[#3a3a3a] sm:text-3xl">
            Please leave us a review!
          </p>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#dadce0] bg-white px-5 py-2.5 shadow-sm">
            <GoogleLogo />
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleSelect(star)}
                onMouseEnter={() => setHover(star)}
                onMouseLeave={() => setHover(0)}
                className="min-h-[44px] min-w-[44px] rounded-sm p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBC05]"
                aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >
                <Star
                  className={`size-7 transition-colors sm:size-8 ${
                    star <= activeStars
                      ? 'fill-[#FBBC05] text-[#FBBC05]'
                      : 'fill-transparent text-[#ccc]'
                  }`}
                />
              </button>
            ))}
          </div>
        </>
      )}

      {stage === 'support' && (
        <form
          onSubmit={(e) => handleSubmit(onSubmit)(e)}
          className="flex w-full max-w-md flex-col items-center gap-4"
          noValidate
        >
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`size-5 ${
                  star <= rating ? 'fill-[#FBBC05] text-[#FBBC05]' : 'fill-transparent text-[#ccc]'
                }`}
                aria-hidden="true"
              />
            ))}
          </div>
          <p className="text-center text-base text-[#555]">
            Give us a chance to help! Shoot us a message and we&apos;ll give you a bell and sort it.
          </p>

          <input type="hidden" {...register('rating', { valueAsNumber: true })} />

          {/* Who it came from, so Paul can ring back. One channel is enough:
              the point is to hear from them, not to qualify a lead. */}
          <div className="grid w-full gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="feedback-first-name"
                className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
              >
                First name
              </label>
              <input
                id="feedback-first-name"
                {...register('firstName')}
                placeholder="First name"
                autoComplete="given-name"
                aria-required="true"
                aria-invalid={!!errors.firstName}
                className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-500" role="alert">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="feedback-last-name"
                className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
              >
                Last name
              </label>
              <input
                id="feedback-last-name"
                {...register('lastName')}
                placeholder="Last name"
                autoComplete="family-name"
                aria-required="true"
                aria-invalid={!!errors.lastName}
                className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-500" role="alert">
                  {errors.lastName.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="feedback-phone"
                className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
              >
                Phone
              </label>
              <input
                id="feedback-phone"
                type="tel"
                inputMode="tel"
                {...register('phone', { deps: ['email'] })}
                placeholder="0400 000 000"
                autoComplete="tel"
                aria-invalid={!!errors.phone}
                className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-500" role="alert">
                  {errors.phone.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="feedback-email"
                className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
              >
                Email
              </label>
              <input
                id="feedback-email"
                type="email"
                {...register('email', { deps: ['phone'] })}
                placeholder="you@example.com"
                autoComplete="email"
                aria-invalid={!!errors.email}
                className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500" role="alert">
                  {errors.email.message}
                </p>
              )}
              <p className="mt-1 text-sm text-[#666]">Either one is enough.</p>
            </div>
          </div>

          <div className="w-full">
            <label
              htmlFor="feedback-message"
              className="font-heading text-brand-purple mb-2 block text-base font-semibold tracking-wider uppercase"
            >
              What went wrong?
            </label>
            <textarea
              id="feedback-message"
              {...register('message')}
              placeholder="Give us hell"
              rows={3}
              autoCapitalize="sentences"
              enterKeyHint="done"
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? 'feedback-message-error' : undefined}
              className="input-rock focus:border-brand-purple focus:ring-brand-purple w-full rounded-sm border border-[#d8cfe0] bg-[#f8f5fa] px-4 py-3 text-[#1a1a1a] placeholder:text-[#aaa] focus:ring-1 focus:outline-none"
            />
            {errors.message && (
              <p id="feedback-message-error" className="mt-1 text-sm text-red-500" role="alert">
                {errors.message.message}
              </p>
            )}
          </div>

          {/* Honeypot: visually hidden, must stay empty */}
          <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          {captchaBypassEnabled ? (
            <p className="text-center font-mono text-xs text-[#888]">
              verification disabled (local dev)
            </p>
          ) : captchaMounted ? (
            <div
              className="w-full"
              style={
                {
                  colorScheme: 'light',
                  '--altcha-max-width': '100%',
                  '--altcha-color-primary': '#8A2585',
                  '--altcha-color-primary-content': '#ffffff',
                  '--altcha-border-radius': '3px',
                } as React.CSSProperties
              }
            >
              <altcha-widget
                ref={captchaRef}
                challenge="/api/captcha/challenge"
                auto="onload"
                configuration='{"hideFooter":true,"hideLogo":true}'
              />
            </div>
          ) : null}

          {response && (
            <ContextResponse
              severity={response.severity}
              message={response.text}
              className="w-full"
            />
          )}

          <CsrfRetry />

          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting || !captchaPayload || !csrfToken}
            className="h-12 w-full sm:w-auto sm:px-8"
          >
            {isSubmitting ? (
              'Sending...'
            ) : (
              <>
                <Send className="mr-1 size-5" aria-hidden="true" />
                Send Feedback
              </>
            )}
          </Button>
        </form>
      )}

      {stage === 'thanks' && response && (
        <ContextResponse severity={response.severity} message={response.text} />
      )}

      {/* Always show the Google link for people who just want to browse reviews */}
      {stage !== 'support' && (
        <a
          href={GOOGLE_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-2 text-sm text-[#5f6368] transition-colors hover:text-[#3a3a3a]"
        >
          <GoogleLogo size={16} />
          See all Google reviews
          <ArrowRight className="size-3" aria-hidden="true" />
        </a>
      )}
    </div>
  )
}
